import React from "react";
import { Link } from "react-router-dom";
import { Star, MessageCircle, Crown } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export function Stars({ value = 0, size = 14 }) {
  const five = (value || 0) / 2;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 10`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, five - i));
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} className="absolute inset-0 text-white/20" />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}><Star size={size} className="text-yellow-400 fill-yellow-400" /></span>
          </span>
        );
      })}
    </span>
  );
}

export function Score({ g, big }) {
  return (
    <div className="flex items-center gap-2">
      <Star size={big ? 20 : 14} className="text-yellow-400 fill-yellow-400" />
      <span className={`font-head font-bold text-white ${big ? "text-2xl" : "text-sm"}`}>{g.rating_count ? g.rating_avg.toFixed(1) : "–"}<span className="text-white/40 text-xs font-normal">/10</span></span>
      <span className="text-[11px] text-white/40">({g.rating_count})</span>
    </div>
  );
}

const MEDAL = ["#facc15", "#cbd5e1", "#d97706"];

export function PodiumCard({ g, first }) {
  return (
    <Link to={`/games/${g.id}`} data-testid={`top-game-${g.rank}`}
      className={`group relative block rounded-3xl overflow-hidden border hover-rise md:aspect-[3/4] ${first ? "aspect-[4/3] border-yellow-400/40" : "aspect-[16/9] border-white/10"}`}
      style={{ boxShadow: `0 20px 60px -30px ${MEDAL[g.rank - 1]}` }}>
      <img src={mediaUrl(g.img)} alt={g.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <span className="absolute top-4 left-4 w-12 h-12 rounded-2xl grid place-items-center font-head text-2xl font-bold text-black" style={{ background: MEDAL[g.rank - 1] }}>
        {g.rank}
      </span>
      {first && <Crown className="absolute top-4 right-4 text-yellow-400" size={26} />}
      <div className="absolute inset-x-0 bottom-0 p-5">
        <Stars value={g.rating_avg} size={first ? 18 : 15} />
        <h3 className={`mt-2 font-bold text-white leading-tight ${first ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>{g.name}</h3>
        <div className="mt-2 flex items-center justify-between gap-2"><Score g={g} big={first} /><span className="text-xs text-white/50 truncate">{g.publisher}</span></div>
      </div>
    </Link>
  );
}

export function SmallCard({ g }) {
  return (
    <Link to={`/games/${g.id}`} data-testid={`rank-game-${g.rank}`} className="group card-surface rounded-xl overflow-hidden hover-rise block">
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={mediaUrl(g.img)} alt={g.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 font-head text-sm font-bold text-white">#{g.rank}</span>
      </div>
      <div className="p-2.5">
        <div className="text-sm font-semibold text-white truncate">{g.name}</div>
        <div className="mt-1 flex items-center gap-1.5"><Star size={12} className="text-yellow-400 fill-yellow-400" /><span className="text-xs font-bold text-white">{g.rating_count ? g.rating_avg.toFixed(1) : "–"}</span><span className="text-[10px] text-white/40">/10</span></div>
      </div>
    </Link>
  );
}

export function GameTile({ g, badge }) {
  return (
    <Link to={`/games/${g.id}`} data-testid={`game-tile-${g.id}`} className="group card-surface rounded-xl overflow-hidden hover-rise block">
      <div className="relative aspect-video overflow-hidden">
        <img src={mediaUrl(g.img)} alt={g.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {badge && <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-bold uppercase tracking-wide">{badge}</span>}
      </div>
      <div className="p-3">
        <div className="font-bold text-white truncate">{g.name}</div>
        <div className="text-xs text-white/40 truncate">{[g.publisher || g.uploader_name, ...(g.genres || []).slice(0, 1)].join(" · ")}</div>
        <div className="mt-2 flex items-center justify-between"><Stars value={g.rating_avg} size={12} /><span className="text-[11px] text-white/40 inline-flex items-center gap-1"><MessageCircle size={11} /> {g.comment_count}</span></div>
      </div>
    </Link>
  );
}
